// ReviewPopup.jsx
import { useState, useEffect } from 'react';
import { Modal, Button, Rate, Input, message } from 'antd';
import { StarOutlined, MessageOutlined } from '@ant-design/icons';
import axios from 'axios';
import drEasyApi from '../../Api/Customapi';
import { useLanguage } from '../../context/LanguageContext';

const { TextArea } = Input;

const ReviewPopup = ({ doctor, user, slug }) => {
  const { t } = useLanguage();
  const [showInitialPopup, setShowInitialPopup] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);

  // Helper function to safely get doctor name
  const getDoctorName = () => {
    try {
      if (!doctor) return t('common.doctor');
      
      // Log the doctor object for debugging
      console.log('Doctor object in getDoctorName:', doctor);
      console.log('Doctor.name type:', typeof doctor.name);
      console.log('Doctor.name value:', doctor.name);
      
      // Handle different possible structures
      if (typeof doctor.name === 'string' && doctor.name.trim() !== '') {
        return doctor.name;
      } 
      
      if (doctor.name && typeof doctor.name === 'object') {
        console.log('Doctor.name is object:', doctor.name);
        if (doctor.name.name && typeof doctor.name.name === 'string') {
          return doctor.name.name;
        }
      }
      
      if (doctor.firstName && typeof doctor.firstName === 'string') {
        const lastName = doctor.lastName && typeof doctor.lastName === 'string' ? ` ${doctor.lastName}` : '';
        return `${doctor.firstName}${lastName}`;
      }
      
      if (doctor.fullName && typeof doctor.fullName === 'string') {
        return doctor.fullName;
      }
      
      return t('common.doctor');
    } catch (error) {
      console.error('Error in getDoctorName:', error);
      return t('common.doctor');
    }
  };

  // Check if we should show the popup when component mounts
  useEffect(() => {
    try {
      console.log('ReviewPopup useEffect - Doctor:', doctor);
      console.log('ReviewPopup useEffect - User:', user);
      
      if (doctor && user) {
        const shouldShowPopup = !doctor.isReviewed && doctor.canReview;
        console.log('Should show popup:', shouldShowPopup);
        
        if (shouldShowPopup) {
          const timer = setTimeout(() => {
            setShowInitialPopup(true);
          }, 1000);
          
          return () => clearTimeout(timer);
        }
      }
    } catch (error) {
      console.error('Error in ReviewPopup useEffect:', error);
    }
  }, [doctor, user]);

  const handleWantToReview = () => {
    setShowInitialPopup(false);
    setShowReviewModal(true);
  };

  const handleLater = () => {
    setShowInitialPopup(false);
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      message.warning(t('review.selectRating'));
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        text: reviewText,
        rating: rating,
        doctorId: doctor.id || doctor.doctorId,
      };
      

      console.log('Review payload:', payload);

      await drEasyApi.post(`/api/Review/AddReview`, payload);
      
      message.success(t('review.submittedSuccessfully'));
      setShowReviewModal(false);
      setRating(0);
      setReviewText('');
      
    } catch (error) {
      console.error('Review submission error:', error);
      message.error(getErrorMessage(error, t('review.submitFailed')));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error, defaultMessage) => {
    try {
      if (error.response?.data?.message) {
        return error.response.data.message;
      }
      return defaultMessage;
    } catch (err) {
      return defaultMessage;
    }
  };

  // Don't render anything if no doctor data
  if (!doctor) {
    console.log('No doctor data, not rendering ReviewPopup');
    return null;
  }

  // Get doctor name safely
  const doctorName = getDoctorName();
  console.log('Final doctor name to render:', doctorName);

  // Ensure doctorName is always a string
  const safeDoctorName = String(doctorName || t('common.doctor'));

  return (
    <>
      {/* Initial Popup asking if user wants to review */}
      <Modal
        open={showInitialPopup}
        title={
          <div className="flex items-center gap-2">
            <StarOutlined className="text-yellow-500" />
            <span>{t('review.reviewDr')} {safeDoctorName}</span>
          </div>
        }
        footer={[
          <Button key="later" onClick={handleLater}>
            {t('review.later')}
          </Button>,
          <Button 
            key="review" 
            type="primary" 
            onClick={handleWantToReview}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t('review.yesWantToReview')}
          </Button>
        ]}
        onCancel={handleLater}
        width={400}
        centered
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            {t('review.shareExperience', { doctorName: safeDoctorName })}
          </p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <MessageOutlined />
            <span>{t('review.onlyTakesMinute')}</span>
          </div>
        </div>
      </Modal>

      {/* Review Modal with stars and text */}
      <Modal
        open={showReviewModal}
        title={
          <div className="flex items-center gap-2">
            <StarOutlined className="text-yellow-500" />
            <span>{t('review.reviewDr')} {safeDoctorName}</span>
          </div>
        }
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setShowReviewModal(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmitReview}
            loading={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t('review.submitReview')}
          </Button>
        ]}
        onCancel={() => setShowReviewModal(false)}
        width={500}
        centered
      >
        <div className="py-4 space-y-6">
          {/* Rating Section */}
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-800 mb-3">
              {t('review.rateExperience')}
            </h3>
            <Rate
              value={rating}
              onChange={setRating}
              style={{ fontSize: '2rem' }}
              className="text-yellow-500"
            />
            <div className="mt-2 text-sm text-gray-500">
              {rating > 0 && (
                <span>
                  {rating === 1 ? t('review.poor') : ''}
                  {rating === 2 ? t('review.fair') : ''}
                  {rating === 3 ? t('review.good') : ''}
                  {rating === 4 ? t('review.veryGood') : ''}
                  {rating === 5 ? t('review.excellent') : ''}
                </span>
              )}
            </div>
          </div>

          {/* Review Text Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('review.shareExperience')} 
            </label>
            <TextArea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder={t('review.tellOthers', { doctorName: safeDoctorName })}
              rows={4}
              maxLength={500}
              showCount
              minLength={1}
              className="resize-none"
            />
          </div>

          {/* Doctor Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center gap-3">
              {doctor.profileImage && typeof doctor.profileImage === 'string' && (
                <img 
                  src={doctor.profileImage} 
                  alt={safeDoctorName}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div>
                <h4 className="font-medium text-gray-800"> {t('common.dr')} {safeDoctorName}</h4>
                <p className="text-sm text-gray-600">
                  {String(doctor.specialization || doctor.specialty || t('common.generalPractice'))}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReviewPopup;